import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, ShieldCheck, Truck, Star, Leaf, HeartHandshake, PenTool, Palette, Layers, Wand2, Gift, Bell, Repeat, TrendingUp, Crown, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { useAdminView } from '@/context/AdminViewContext';
import SEO from '@/components/SEO';
import SignedInHome from '@/pages/SignedInHome';

const WHY = [
  { icon: PenTool, tk: 'landing.whyEasyTitle', dk: 'landing.whyEasyDesc' },
  { icon: Sparkles, tk: 'landing.whyQualityTitle', dk: 'landing.whyQualityDesc' },
  { icon: Truck, tk: 'landing.whyFastTitle', dk: 'landing.whyFastDesc' },
  { icon: ShieldCheck, tk: 'landing.whySecureTitle', dk: 'landing.whySecureDesc' },
];

const APP_FEATURES = [
  { icon: Palette, tk: 'landing.featLibraryTitle', dk: 'landing.featLibraryDesc' },
  { icon: Wand2, tk: 'landing.featAiTitle', dk: 'landing.featAiDesc' },
  { icon: Layers, tk: 'landing.featSaveTitle', dk: 'landing.featSaveDesc' },
  { icon: Gift, tk: 'landing.featGiftTitle', dk: 'landing.featGiftDesc' },
  { icon: TrendingUp, tk: 'landing.featLoyaltyTitle', dk: 'landing.featLoyaltyDesc' },
  { icon: Bell, tk: 'landing.featTrackTitle', dk: 'landing.featTrackDesc' },
  { icon: Repeat, tk: 'landing.featReorderTitle', dk: 'landing.featReorderDesc' },
  { icon: HeartHandshake, tk: 'landing.featSupportTitle', dk: 'landing.featSupportDesc' },
];

const EARN = [
  { icon: TrendingUp, tk: 'landing.earnSellTitle', dk: 'landing.earnSellDesc' },
  { icon: Crown, tk: 'landing.earnPremiumTitle', dk: 'landing.earnPremiumDesc' },
  { icon: Users, tk: 'landing.earnCorpTitle', dk: 'landing.earnCorpDesc' },
];

const PRODUCTS = [
  { tk: 'landing.phoneCasesTitle', dk: 'landing.phoneCasesDesc', fallback: 'Phone Cases', img: 'https://images.unsplash.com/photo-1535469145415-8e49c30eae75?w=800&q=80', to: '/shop/phone_case', fromUsd: 19.99 },
  { tk: 'landing.stickersTitle', dk: 'landing.stickersDesc', fallback: 'Stickers', img: 'https://images.unsplash.com/photo-1627618997007-6d81f1efb811?w=800&q=80', to: '/shop/sticker', fromUsd: 3.99 },
  { tk: 'landing.labelsTitle', dk: 'landing.labelsDesc', fallback: 'Labels', img: 'https://images.unsplash.com/photo-1706111577676-4e509572b8af?w=800&q=80', to: '/shop/label', fromUsd: 3.99 },
];

const Landing = () => {
  const { format } = useCurrency();
  const { t, lang } = useLanguage();
  const { user, loading } = useAuth();
  const { isAdminAsCustomer } = useAdminView();

  // While auth state is resolving, render nothing to prevent the marketing
  // hero from flashing before we know whether to show the personalized home.
  if (loading) return <div className="min-h-[60vh]" />;

  // Signed-in visitors get the personalized dashboard-style home. Admins keep
  // the marketing landing so their /admin flow is unaffected — unless they've
  // toggled "view as customer" from the header (eye icon), in which case they
  // see the same signed-in home a regular customer sees.
  if (user && (user.role !== 'admin' || isAdminAsCustomer)) return <SignedInHome />;

  return (
    <div>
      <SEO
        title="Jwalek — Design it your way, we print it perfectly"
        description="Custom-printed iPhone cases, stickers, and labels. Design your own with our AI-powered editor or pick from thousands of templates. Fast delivery worldwide."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Jwalek",
          "url": "https://jwalek.com",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://jwalek.com/shop?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl" style={{ background: 'var(--jw-gradient)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="inline-flex">
              <Badge variant="secondary" className="jw-panel jw-text-muted">{t('landing.aiBadge', '✨ Now with AI Design Generator')}</Badge>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }} className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mt-4 jw-text-primary">
              {lang === 'ar' ? (
                <>صمّم بأسلوبك،<br /><span className="jw-gradient-text">ونحن نطبعه بإتقان.</span></>
              ) : (
                <>Design it your way,<br /><span className="jw-gradient-text">we print it perfectly.</span></>
              )}
            </motion.h1>
            <p className="jw-text-muted text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
              {lang === 'ar'
                ? 'جواليك هي منصتك الشاملة لطباعة أغطية آيفون والملصقات واللاصقات المخصصة. اختر من آلاف التصاميم أو أنشئ تصميمك بمحررنا المدعوم بالذكاء الاصطناعي.'
                : 'Jwalek is your all-in-one platform for custom printing of iPhone cases, stickers and labels. Choose from thousands of designs or create your own with our AI\u2011powered editor.'}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/shop">
                <Button data-testid="hero-start-designing-button" className="jw-btn-glossy rounded-[14px] h-12 px-6 text-base">
                  {t('landing.startDesigning', 'Start Designing')} <ArrowRight className="ml-2 w-4 h-4 jw-flip-in-rtl" />
                </Button>
              </Link>
              <Link to="/designs">
                <Button data-testid="hero-browse-designs-button" className="jw-btn-gloss-ghost h-12 px-6 rounded-[14px]">
                  {t('landing.browseDesigns', 'Browse Designs')}
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-4 mt-8">
              <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}</div>
              <span className="jw-text-muted text-sm"><b className="jw-text-primary">4.8/5</b> {t('landing.rating', '· 10,000+ happy customers')}</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="relative">
            <div className="grid grid-cols-3 gap-3">
              <img src="https://images.unsplash.com/photo-1535469145415-8e49c30eae75?w=400&q=80" alt="case" className="rounded-2xl aspect-[3/4] object-cover shadow-2xl -translate-y-6" />
              <img src="https://images.unsplash.com/photo-1627618997007-6d81f1efb811?w=400&q=80" alt="sticker" className="rounded-2xl aspect-[3/4] object-cover shadow-2xl translate-y-4" />
              <img src="https://images.unsplash.com/photo-1706111577676-4e509572b8af?w=400&q=80" alt="label" className="rounded-2xl aspect-[3/4] object-cover shadow-2xl -translate-y-2" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold jw-text-primary">{t('landing.printAnything', 'Print anything.')}</h2>
            <p className="jw-text-muted mt-2">{t('landing.printAnythingSub', 'Three product lines, unlimited possibilities.')}</p>
          </div>
          <Link to="/shop" className="jw-text-muted hover:opacity-80 text-sm inline-flex items-center gap-1" data-testid="landing-see-all-products-link">{t('landing.seeAll', 'See all')} <ArrowRight className="w-4 h-4 jw-flip-in-rtl" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PRODUCTS.map((p) => {
            const title = t(p.tk, p.fallback);
            return (
            <Link key={p.fallback} to={p.to} data-testid={`landing-product-${p.fallback.toLowerCase().replace(' ', '-')}-card`}>
              <Card className="jw-card overflow-hidden group">
                <div className="aspect-[4/3] overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
                  <img src={p.img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-xl font-semibold jw-text-primary">{title}</h3>
                    <Badge variant="secondary" className="jw-text-muted">{`${t('landing.from', 'From')} ${format(p.fromUsd)}`}</Badge>
                  </div>
                  <p className="jw-text-muted text-sm mt-2">{t(p.dk, '')}</p>
                  <div className="mt-4 inline-flex items-center jw-text-muted text-sm group-hover:opacity-80">
                    {t('landing.explore', 'Explore')} <ArrowRight className="ml-1 w-4 h-4 jw-flip-in-rtl" />
                  </div>
                </div>
              </Card>
            </Link>
          );})}
        </div>
      </section>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center jw-text-primary">{t('landing.whyTitle', 'Why')} <span className="jw-gradient-text">Jwalek</span>{t('landing.whyQuestion', '?')}</h2>
        <p className="jw-text-muted text-center mt-2 max-w-xl mx-auto">{t('landing.whySub', 'Premium quality, seamless creation, and delightful delivery — every time.')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {WHY.map((w) => (
            <div key={w.tk} className="jw-card p-5">
              <div className="w-10 h-10 rounded-lg grid place-items-center jw-gradient-bg text-white"><w.icon className="w-5 h-5" /></div>
              <h3 className="font-semibold mt-4 jw-text-primary">{t(w.tk, '')}</h3>
              <p className="jw-text-muted text-sm mt-1">{t(w.dk, '')}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-bold jw-text-primary">{t('landing.everythingTitle', 'Everything you need to create & sell.')}</h2>
        <p className="jw-text-muted mt-2 max-w-xl">{t('landing.everythingSub', 'A powerful design editor, a rich library of templates, and thoughtful features that make custom printing effortless.')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {APP_FEATURES.map((f) => (
            <div key={f.tk} className="jw-card p-5">
              <f.icon className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold mt-3 jw-text-primary">{t(f.tk, '')}</h3>
              <p className="jw-text-muted text-sm mt-1">{t(f.dk, '')}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Earn */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-bold jw-text-primary">{t('landing.earnTitle', 'Earn & save with Jwalek.')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
          {EARN.map((e) => (
            <div key={e.tk} className="jw-card p-6 relative overflow-hidden">
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-25 blur-2xl jw-gradient-bg" />
              <e.icon className="w-6 h-6 text-pink-500 relative" />
              <h3 className="font-display text-xl font-semibold mt-4 relative jw-text-primary">{t(e.tk, '')}</h3>
              <p className="jw-text-muted text-sm mt-1 relative">{t(e.dk, '')}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { icon: ShieldCheck, tk: 'landing.trustQuality' },
            { icon: Repeat, tk: 'landing.trustReturns' },
            { icon: ShieldCheck, tk: 'landing.trustPayments' },
            { icon: Truck, tk: 'landing.trustLocal' },
            { icon: Leaf, tk: 'landing.trustEco' },
          ].map((b) => (
            <div key={b.tk} className="jw-panel p-4 flex items-center gap-3">
              <b.icon className="w-5 h-5 text-emerald-500" />
              <span className="text-sm jw-text-muted">{t(b.tk, '')}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="relative overflow-hidden rounded-[24px] jw-card p-10 sm:p-14">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full opacity-30 blur-3xl jw-gradient-bg" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="font-display text-3xl sm:text-4xl font-bold jw-text-primary">{t('landing.ctaTitle', 'Bring your ideas to life.')}</h3>
              <p className="jw-text-muted mt-2 max-w-xl">{t('landing.ctaSub', 'Design now, print perfectly. Get started in seconds.')}</p>
            </div>
            <div className="relative inline-flex jw-halo rounded-[14px]">
              <Link to="/shop">
                <Button data-testid="landing-get-started-button" className="jw-btn-glossy rounded-[14px] h-12 px-8 text-base">{t('landing.getStarted', 'Get Started')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
